import { useEffect, useRef, useCallback } from 'react';
import { useTunerStore } from '../stores/tunerStore.js';
import { detectPitch } from '../audio/pitch/detectPitch.js';

const NOTE_STRINGS = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

/** Below this the classic 2048 window and 50 Hz floor are not enough (8/9/10-string tunings reach ~23 Hz). */
const STANDARD_FLOOR_HZ = 50;
/** Targets this low are often inaudible to phone mics; the octave harmonic is accepted instead. */
const HARMONIC_FALLBACK_HZ = 45;

/** Lowest string frequency of the selected preset, scaled for the A4 reference. */
const lowestPresetHz = (referenceA4: number): number => {
  const { selectedPreset } = useTunerStore.getState();
  return Math.min(...selectedPreset.notes.map((n) => n.frequency)) * (referenceA4 / 440);
};

/** The readout is cleared when no pitch has been detected for this long (FND-19). */
const READOUT_HOLD_MS = 400;

/** Analyser window for the preset: 2048 as before for normal guitars, bigger only when the lowest note needs it. */
const fftSizeFor = (lowestHz: number, sampleRate: number): number => {
  if (lowestHz >= STANDARD_FLOOR_HZ) return 4096;
  const needed = (3 * sampleRate) / lowestHz;
  return Math.min(16384, Math.max(4096, 2 ** Math.ceil(Math.log2(needed))));
};

/** Map a getUserMedia failure to an accurate Persian message (not every failure is a denied permission). */
function microphoneErrorMessage(err: unknown): string {
  const name = err instanceof DOMException ? err.name : '';
  // A Permissions-Policy header (e.g. `microphone=()` from the host) blocks the mic regardless of browser settings
  const policy = (document as any).permissionsPolicy ?? (document as any).featurePolicy;
  if (policy?.allowsFeature && !policy.allowsFeature('microphone')) {
    return 'میکروفون توسط تنظیمات امنیتی سرور مسدود شده است (Permissions-Policy). این مشکل از مرورگر شما نیست.';
  }
  if (!navigator.mediaDevices?.getUserMedia) {
    return 'مرورگر شما از میکروفون پشتیبانی نمی‌کند یا صفحه روی اتصال امن (HTTPS) باز نشده است.';
  }
  if (name === 'NotFoundError' || name === 'OverconstrainedError') {
    return 'میکروفونی پیدا نشد. لطفاً یک میکروفون وصل کنید.';
  }
  if (name === 'NotReadableError' || name === 'AbortError') {
    return 'میکروفون در دسترس نیست؛ ممکن است برنامه‌ی دیگری از آن استفاده کند.';
  }
  return 'دسترسی به میکروفون امکان‌پذیر نشد. لطفاً مجوز میکروفون را فعال کنید.';
}

export const useAudioTuner = () => {
  const {
    isListening,
    setIsListening,
    setPitchData,
    setErrorMessage
  } = useTunerStore();

  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const lastRunRef = useRef(0);
  const lastPitchAtRef = useRef(0);

  const updatePitch = useCallback(() => {
    if (!analyserRef.current) return;

    const analyser = analyserRef.current;
    const sampleRate = audioContextRef.current?.sampleRate || 44100;
    const referenceA4 = useTunerStore.getState().referenceA4;
    const lowestHz = lowestPresetHz(referenceA4);
    const wantedFft = fftSizeFor(lowestHz, sampleRate);
    if (analyser.fftSize !== wantedFft) analyser.fftSize = wantedFft;
    // Big windows are expensive to correlate: look every ~80 ms instead of every frame
    if (wantedFft >= 8192) {
      const now = performance.now();
      if (now - lastRunRef.current < 80) {
        animationFrameRef.current = requestAnimationFrame(updatePitch);
        return;
      }
      lastRunRef.current = now;
    }
    const buffer = new Float32Array(analyser.fftSize);
    analyser.getFloatTimeDomainData(buffer);

    const minFreq = Math.max(18, Math.min(STANDARD_FLOOR_HZ, lowestHz * 0.8));
    const result = detectPitch(buffer, sampleRate, { minHz: minFreq, maxHz: 1200 });
    const frequency = result ? result.frequency : -1;
    const nowMs = performance.now();

    if (frequency > Math.min(40, minFreq) && frequency < 1200) {
      lastPitchAtRef.current = nowMs;
      // Calculate note parameters
      const semitoneOffset = 12 * Math.log2(frequency / referenceA4);
      const roundedSemitone = Math.round(semitoneOffset);
      const targetFrequency = referenceA4 * Math.pow(2, roundedSemitone / 12);
      // With a string selected, measure deviation against that string's target pitch; otherwise nearest chromatic note
      const { selectedString, selectedPreset } = useTunerStore.getState();
      const stringTarget = selectedString !== null
        ? selectedPreset.notes.find((n) => n.stringNumber === selectedString)
        : undefined;
      let centsReference = stringTarget
        ? stringTarget.frequency * (referenceA4 / 440)
        : targetFrequency;
      // Phone mics barely pick up fundamentals under ~45 Hz: the octave above is what is heard, so compare against it
      if (stringTarget && centsReference < HARMONIC_FALLBACK_HZ && frequency > centsReference * 1.5) {
        centsReference *= 2;
      }
      const cents = Math.round(1200 * Math.log2(frequency / centsReference));

      // Calculate note name and octave
      const midiNumber = 69 + roundedSemitone;
      const noteIndex = ((midiNumber % 12) + 12) % 12;
      const noteName = NOTE_STRINGS[noteIndex];
      const octave = Math.floor(midiNumber / 12) - 1;

      setPitchData(
        noteName,
        octave,
        parseFloat(frequency.toFixed(1)),
        Math.max(-50, Math.min(50, cents))
      );
    } else if (nowMs - lastPitchAtRef.current > READOUT_HOLD_MS && useTunerStore.getState().detectedNote !== null) {
      // Silence or unpitched noise: drop the stale note instead of freezing it on screen
      useTunerStore.setState({ detectedNote: null, detectedOctave: null, detectedFrequency: 0, centsDeviation: 0, isInTune: false });
    }

    animationFrameRef.current = requestAnimationFrame(updatePitch);
  }, [setPitchData]);

  const startListening = async () => {
    setErrorMessage(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false
        }
      });

      mediaStreamRef.current = stream;
      const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
      audioContextRef.current = audioCtx;

      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 2048;
      source.connect(analyser);
      analyserRef.current = analyser;

      lastPitchAtRef.current = performance.now();
      setIsListening(true);
      animationFrameRef.current = requestAnimationFrame(updatePitch);
    } catch (err) {
      console.warn('Microphone stream access unavailable:', err);
      setIsListening(false);
      setErrorMessage(microphoneErrorMessage(err));
    }
  };

  const stopListening = () => {
    setErrorMessage(null);
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }

    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    useTunerStore.setState({ detectedNote: null, detectedOctave: null, detectedFrequency: 0, centsDeviation: 0, isInTune: false });
    setIsListening(false);
  };

  useEffect(() => {
    return () => {
      stopListening();
    };
  }, []);

  // FND-40: never keep the microphone open in a background tab
  useEffect(() => {
    const release = () => {
      if (useTunerStore.getState().isListening) stopListening();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') release();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', release);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', release);
    };
  }, []);

  return {
    isListening,
    startListening,
    stopListening
  };
};
